/**
 * Generative avatars in the "marble" style of boringavatars.com — smooth
 * organic gradient blobs, deterministic from a seed. Dependency-free and local
 * (the boring-avatars marble algorithm, reimplemented as an SVG string).
 *
 *   avatarSvgDataUrl(seed, i)   -> data: URL for <img src> (display)
 *   avatarToPngDataUrl(seed, i) -> Promise<pngDataUrl> for the upload flow
 *      (the profile-image API posts a base64 image, same as a file upload; the
 *       SVG is same-origin so the canvas is never tainted).
 */

const SIZE = 80;
const ELEMENTS = 3;

// On-brand palette (the accent colours used across the app) so the marble reads
// as part of the product rather than a random identicon.
const PALETTE = ['#5AA5EF', '#6D5EFC', '#2FD0C8', '#F0A95A', '#F05A7D', '#7DB3F2'];

const hashCode = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getDigit = (number, ntn) => Math.floor((number / (10 ** ntn)) % 10);

const getUnit = (number, range, index) => {
  const value = number % range;
  if (index && getDigit(number, index) % 2 === 0) return -value;
  return value;
};

const marbleProps = (seed) => {
  const num = hashCode(seed);
  return Array.from({ length: ELEMENTS }, (_, i) => ({
    color: PALETTE[(num + i) % PALETTE.length],
    translateX: getUnit(num * (i + 1), SIZE / 10, 1),
    translateY: getUnit(num * (i + 1), SIZE / 10, 2),
    scale: 1.2 + getUnit(num * (i + 1), SIZE / 20) / 10,
    rotate: getUnit(num * (i + 1), 360, 1),
  }));
};

export const avatarSvgString = (seed, index, size = 80) => {
  const p = marbleProps(`${seed || 'projector'}::${index}`);
  const id = `m${index}`;
  const c = SIZE / 2;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${SIZE} ${SIZE}" width="${size}" height="${size}">`
    + `<mask id="msk_${id}" maskUnits="userSpaceOnUse" x="0" y="0" width="${SIZE}" height="${SIZE}">`
    + `<rect width="${SIZE}" height="${SIZE}" fill="#fff"/></mask>`
    + `<g mask="url(#msk_${id})">`
    + `<rect width="${SIZE}" height="${SIZE}" fill="${p[0].color}"/>`
    + `<path filter="url(#flt_${id})" d="M32.414 59.35L50.376 70.5H72.5v-71H33.728L26.5 13.381l4.596 17.185Z" `
    + `fill="${p[1].color}" transform="translate(${p[1].translateX} ${p[1].translateY}) rotate(${p[1].rotate} ${c} ${c}) scale(${p[2].scale})"/>`
    + `<path filter="url(#flt_${id})" style="mix-blend-mode:overlay" d="M22.216 24L0 46.75l14.108 38.129L78 86l-3.081-59.276-22.378 4.005 12.972 20.186-23.35 27.395L22.215 24Z" `
    + `fill="${p[2].color}" transform="translate(${p[2].translateX} ${p[2].translateY}) rotate(${p[2].rotate} ${c} ${c}) scale(${p[2].scale})"/>`
    + '</g>'
    + `<defs><filter id="flt_${id}" x="-20" y="-20" width="120" height="120" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">`
    + '<feFlood flood-opacity="0" result="BackgroundImageFix"/>'
    + '<feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>'
    + '<feGaussianBlur stdDeviation="7" result="effect1_foregroundBlur"/></filter></defs>'
    + '</svg>'
  );
};

const toDataUrl = (svg) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;

export const avatarSvgDataUrl = (seed, index, size = 80) => toDataUrl(avatarSvgString(seed, index, size));

export const avatarToPngDataUrl = (seed, index, size = 256) => new Promise((resolve, reject) => {
  try {
    const svgUrl = toDataUrl(avatarSvgString(seed, index, size));
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = svgUrl;
  } catch (e) {
    reject(e);
  }
});
